import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { SortDescriptor } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridSortChangeEvent,
} from '@progress/kendo-react-grid'
import { CreateFilter } from 'api/Identity/Types/CreateFilter'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { PageData } from 'api/Pagination/Types'
import { DividendPaymentDataDto } from 'api/Trading-Accounts/Types/DividendsDto'
import AddButton from 'components/AddButton/AddButton'
import { Box } from 'components/Box'
import { Pager } from 'components/components/Pager'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { FilterType } from 'components/Filter'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { getDividendFields } from 'components/Filter/tradingAccountDividendFields'
import MoreButton from 'components/MoreButton/MoreButton'
import { errorToast, successToast, ToastContext } from 'components/Toast/Toast'
import { createGridState, useGridFilter, useGridState, useGridStatusState } from 'features/DataGrid'
import { isGoBack } from 'lib/react-router-dom'
import { useAppDispatch, useAppSelector } from 'store'
import {
  CompositeFilterDescriptor,
  GridFilterActionState,
  selectGridFilterByFilterId,
} from 'store/grid'
import {
  fetchFilterAction,
  fetchFilterTypesAction,
  removeFilterAction,
  saveFilterAction,
  updateFilterAction,
} from 'store/identity/identity.actions'
import {
  fetchInstrumentsAction,
  getCorporateActionTypes,
  getCurrenciesAction,
  getDividendSubTypesAction,
} from 'store/trading-accounts/trading-accounts.actions'
import { PageQuery } from 'utils/api'
import { useApiClient } from 'utils/api/ApiClient'
import { IdentityApiClient } from 'utils/api/identityApi'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { hasPermission } from 'utils/permissionsValidator'
import { buildQuery, buildSortQuery } from 'utils/QueryHelpers'
import { TableWithExport } from 'utils/table/withExport'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import { CustomColumnMenu } from '../../Records/Clients/CustomColumnMenu'
import { AddEditDividendForm, AddEditDividendFormValues } from './AddEditDividendForm'
import customCorporateActionColumns from './customCorporateActionsColumn'
import { GridColumn } from './dividendsColumn'
import { DividendsTab } from './DividendsNavigation'
import { normalizeDividendSortQuery } from './normalizers/normalizeDividendSortQuery'
import { UploadDividendModal } from './UploadDividendModal'

const useMapStateToProps = () => ({
  filters: useAppSelector((state) => state.identity.filter),
  filterTypes: useAppSelector((state) => state.identity.filterType),
  gridFilterByFilterIdSelector: useAppSelector(selectGridFilterByFilterId),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const identityApiClient = useApiClient(IdentityApiClient)
  return {
    fetchFilterTypes: async () => dispatch(fetchFilterTypesAction(identityApiClient)),
    fetchFilters: async () => dispatch(fetchFilterAction(identityApiClient)),
    saveFilter: async (data: CreateFilter) => dispatch(saveFilterAction(data, identityApiClient)),
    updateFilter: async (filterId: string, data: CreateFilter) =>
      dispatch(updateFilterAction(filterId, data, identityApiClient)),
    removeFilter: async (id: string) => dispatch(removeFilterAction(id, identityApiClient)),
  }
}

const CustomCorporateActionPage = (): JSX.Element => {
  const [auth] = useContext(AuthSessionContext)

  const dispatch = useAppDispatch()

  const [columns, setColumns] = useState<GridColumn[]>([])
  const [dividends, setDividends] = useState<DividendPaymentDataDto[]>([])
  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })
  const [uploadDividendModal, setUploadDividendModal] = useState(false)

  const setToast = useContext(ToastContext)
  const [isAddEditDividend, setAddEditDividend] = useState<{
    data: DividendPaymentDataDto | undefined
    visible: boolean
    isEdit?: boolean
  }>({
    data: undefined,
    visible: false,
  })
  const [isDeleteDividend, setDeleteDividend] = useState<{
    data: DividendPaymentDataDto | undefined
    visible: boolean
  }>({
    data: undefined,
    visible: false,
  })

  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { gridState, gridActions } = useGridState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)

  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  const dividendCurrencies = useAppSelector((state) => state.tradingAccounts.currencies)
  const brokerSymbols = useAppSelector((state) => state.tradingAccounts.instruments)
  const corporateActionTypes = useAppSelector((state) => state.tradingAccounts.corporateActionTypes)
  const dividendsSubTypes = useAppSelector((state) => state.tradingAccounts.dividendSubTypes)

  const permissions = auth?.permissions || []

  const { filters, filterTypes } = useMapStateToProps()

  const { fetchFilterTypes, fetchFilters } = useMapDispatchToProps()

  useEffect(() => {
    if (isEmpty(brokerSymbols)) dispatch(fetchInstrumentsAction(tradingAccountsApiClient))
    if (isEmpty(dividendCurrencies)) dispatch(getCurrenciesAction(tradingAccountsApiClient))
    if (isEmpty(corporateActionTypes)) dispatch(getCorporateActionTypes(tradingAccountsApiClient))
    if (isEmpty(dividendsSubTypes)) dispatch(getDividendSubTypesAction(tradingAccountsApiClient))
  }, [])

  const fetchGrid = async (
    callback: (
      gridFilterState: GridFilterActionState
    ) => Promise<PageData<DividendPaymentDataDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )
    const response = await callback(gridFilterState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  const handleFetchGrid = async () => {
    await fetchGrid(
      async (
        gridFilterState: GridFilterActionState
      ): Promise<PageData<DividendPaymentDataDto[]>> => {
        const searchQuery = getSearchQuery(gridFilterState.filter)
        const sortQuery = getSort(gridFilterState.sort)
        const response = await tradingAccountsApiClient.getDividends({
          search: searchQuery,
          sort: sortQuery,
          dividendPaymentDataType: 2,
          pageIndex: gridFilterState.pageIndex,
          pageSize: gridFilterState.pageSize,
          skipCount: tableData?.skipCount !== false,
        })

        if (isEmpty(filterTypes)) await fetchFilterTypes()
        if (isEmpty(filters)) await fetchFilters()

        setColumns(customCorporateActionColumns)
        if (response?.items) {
          setDividends(response.items)
        }
        setTableData({ ...tableData, meta: response })

        return response
      }
    )
  }

  useEffect(() => {
    handleFetchGrid()
  }, [])

  const getSearchQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      return buildQuery(filter ?? gridState.filter) ?? null
    },
    [gridState.filter]
  )

  const getSort = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null => {
      return buildSortQuery(normalizeDividendSortQuery(sortDescriptors ?? gridState.sort))
    },
    [gridState.sort]
  )

  const filterFetch = useCallback(
    async (query?: PageQuery): Promise<void> => {
      const searchQuery = query?.search !== undefined ? query.search : getSearchQuery()
      const sortQuery = query?.sort ?? getSort(gridState.sort)
      const pageIndex = query?.pageIndex ?? gridState.pageIndex ?? 1
      const pageSize = query?.pageSize ?? gridState.pageSize ?? 10
      const take = pageSize
      const skip = (pageIndex - 1) * pageSize
      const response = await tradingAccountsApiClient.getDividends({
        search: searchQuery,
        sort: sortQuery,
        dividendPaymentDataType: 2,
        pageIndex,
        pageSize,
        skipCount: tableData?.skipCount !== false,
      })

      const total = response.totalResults

      if (response?.items) {
        setDividends(response.items)
      }
      gridActions.setGridFilters({
        take,
        skip,
        total,
        pageIndex,
      })
      setTableData({ ...tableData, meta: response })
    },
    [
      getSearchQuery,
      getSort,
      gridActions,
      gridState.pageSize,
      gridState.sort,
      gridState.take,
      tableData?.skipCount,
    ]
  )

  const handleColumnsSubmit = (columnsState: GridColumn[]) => {
    setColumns(columnsState)
  }

  const handleSortChange = async (event: GridSortChangeEvent) => {
    gridActions.setGridFilters({
      sort: event.sort,
    })

    gridStatusActions.setFetching(true)
    const searchQuery = getSearchQuery(gridState.filter)
    const sortQuery = getSort(event.sort)
    const response = await tradingAccountsApiClient.getDividends({
      search: searchQuery,
      sort: sortQuery,
      dividendPaymentDataType: 2,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
    })

    gridStatusActions.setFetching(false)

    if (response?.items) {
      setDividends(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleSkipCountChange = async (skipCount: boolean) => {
    gridStatusActions.setFetching(true)
    const searchQuery = getSearchQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await tradingAccountsApiClient.getDividends({
      search: searchQuery,
      dividendPaymentDataType: 2,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount,
    })
    const total = response.totalResults

    gridStatusActions.setFetching(false)

    if (response?.items) {
      setDividends(response.items)
    }
    gridActions.setGridFilters({
      total,
    })
    setTableData({ ...tableData, skipCount, meta: response })
  }

  const handleGridPageChange = async (page: number) => {
    gridStatusActions.setFetching(true)
    const searchQuery = getSearchQuery()
    const sortQuery = getSort(gridState.sort)
    const pageIndex = page
    gridActions.setGridFilters({
      pageIndex,
    })

    const response = await tradingAccountsApiClient.getDividends({
      search: searchQuery,
      sort: sortQuery,
      dividendPaymentDataType: 2,
      pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
    })
    const total = response.totalResults

    gridStatusActions.setFetching(false)

    gridActions.setGridFilters({
      total,
    })
    if (response?.items) {
      setDividends(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    gridStatusActions.setFetching(true)
    await gridActions.setGridFilters({
      ...gridState,
      pageSize,
    })

    const searchQuery = getSearchQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await tradingAccountsApiClient.getDividends({
      search: searchQuery,
      sort: sortQuery,
      dividendPaymentDataType: 2,
      pageIndex: 1,
      pageSize,
      skipCount: tableData?.skipCount !== false,
    })
    const total = response.totalResults

    gridStatusActions.setFetching(false)

    gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      take: pageSize,
      total,
    })
    if (response?.items) {
      setDividends(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleEdit = (dividend: DividendPaymentDataDto) => {
    setAddEditDividend({ visible: true, data: dividend, isEdit: true })
  }

  const handleDeleteConfirmation = (dividend: DividendPaymentDataDto) => {
    setDeleteDividend({ data: dividend, visible: true })
  }

  const handleConfirmationDelete = async () => {
    if (isDeleteDividend.data) {
      try {
        await tradingAccountsApiClient.deleteDividend(isDeleteDividend.data.id)
        setToast(successToast('Dividend has been deleted.'))
        await filterFetch()

        handleAddEditCancel()
        handleConfirmationCancel()
      } catch (error) {
        console.error('Error while deleting dividend: ', error)
        handleAddEditCancel()
        handleConfirmationCancel()
      }
    }
  }

  const handleAddEditCancel = () => {
    setAddEditDividend({ data: undefined, visible: false, isEdit: undefined })
  }

  const handleConfirmationCancel = () => {
    setDeleteDividend({ visible: false, data: undefined })
  }

  const onSubmit = async (values: AddEditDividendFormValues) => {
    if (!hasPermission(permissions, PermissionsEnum.CanEditDividendPayments))
      return setToast(errorToast('Permission denied'))
    if (values.id) {
      await tradingAccountsApiClient.updateDividend(values.id, { ...values, id: values.id })
    } else {
      //
    }
    setToast(successToast(`Dividend has been ${values.id ? 'updated' : 'created'}`))
    setAddEditDividend({ visible: false, data: undefined, isEdit: undefined })
    await filterFetch()
  }

  return (
    <React.Fragment>
      <Breadcrumbs
        crumbs={[
          {
            title: 'Brokerage - Custom Corporate Events',
            link: links.brokerageDividends.url.replace(':slug', DividendsTab.CustomCorporate),
          },
        ]}
      />
      <SelectFiltersFacade
        gridState={gridState}
        gridStatusActions={gridStatusActions}
        gridStatusState={gridStatusState}
        setGridFilters={gridActions.setGridFilters}
        filterFetch={filterFetch}
        filterType={FilterType.CustomDividendsOverview}
        filterFields={[...getDividendFields()]}
        containerClassName='dividends-table__filter'
        getQuery={getSearchQuery}
      />
      <Box display='flex' flexDirection='row'>
        <AddButton
          text='Upload CSV'
          disabled={
            !hasPermission(permissions, PermissionsEnum.CanUploadCsvFilesToCreateDividendPayments)
          }
          onClick={() => setUploadDividendModal(true)}
        />
      </Box>
      <TableStateContext.Provider value={[tableData, setTableData]}>
        <TableWithExport data={dividends} columns={columns}>
          <Grid
            {...gridState}
            resizable
            data={dividends}
            sort={gridState.sort}
            total={gridState.total}
            take={gridState.take}
            skip={gridState.skip}
            onSortChange={handleSortChange}
            pageable
            pager={() => (
              <Pager
                onSkipCountChange={handleSkipCountChange}
                onPageChange={handleGridPageChange}
                onPageSizeChange={handlePageSizeChange}
                page={gridState.pageIndex}
                pageSize={gridState.pageSize}
                take={gridState.take || 10}
                skip={gridState.skip || 0}
              />
            )}
            style={{ zIndex: 0 }}
          >
            {columns
              .filter((c) => c.show)
              .map((column, idx) => (
                <Column
                  key={idx + 2}
                  {...column}
                  columnMenu={(props) => (
                    <CustomColumnMenu
                      {...props}
                      columns={columns}
                      onColumnsSubmit={handleColumnsSubmit}
                    />
                  )}
                />
              ))}
            <Column
              width={50}
              title=''
              cell={(props: GridCellProps) => (
                <td>
                  <MoreButton
                    items={[
                      {
                        name: 'Edit',
                        'data-test': 'edit',
                        disabled: !hasPermission(
                          permissions,
                          PermissionsEnum.CanEditDividendPayments
                        ),
                        onClick: () => handleEdit(props.dataItem),
                      },
                      {
                        name: 'Delete',
                        'data-test': 'delete',
                        disabled: !hasPermission(
                          permissions,
                          PermissionsEnum.CanEditDividendPayments
                        ),
                        onClick: () => handleDeleteConfirmation(props.dataItem),
                      },
                    ]}
                    data-test='more-button'
                  />
                </td>
              )}
            />
          </Grid>
        </TableWithExport>
      </TableStateContext.Provider>
      {isDeleteDividend.data && (
        <ConfirmationModal
          yesButtonText='Yes, delete'
          onYes={handleConfirmationDelete}
          onCancel={handleConfirmationCancel}
          visible={isDeleteDividend.visible}
          renderTitle={
            <div>
              <p>
                Are you sure you want to
                <br />
                delete the following dividend record?
              </p>

              <div>
                <div id='field'>
                  <strong>Broker Symbol</strong> {isDeleteDividend.data.exchangeSymbol}
                </div>
                <div id='field'>
                  <strong>Amount</strong> {isDeleteDividend.data.amount}{' '}
                  {isDeleteDividend.data.currency.name}
                </div>
                <div id='field'>
                  <strong>Ex-Dividend Date</strong> {isDeleteDividend.data.eventDate}
                </div>
              </div>
            </div>
          }
        />
      )}
      {isAddEditDividend.visible && (
        <AddEditDividendForm
          isOpen={isAddEditDividend.visible}
          onClose={handleAddEditCancel}
          permissions={permissions}
          dividend={isAddEditDividend.data}
          brokerSymbols={brokerSymbols}
          dividendCurrencies={dividendCurrencies}
          dividendTypes={corporateActionTypes}
          dividendSubTypes={dividendsSubTypes}
          onSubmit={onSubmit}
          isEdit={isAddEditDividend.isEdit}
        />
      )}
      {uploadDividendModal && (
        <UploadDividendModal
          permissions={permissions}
          onSubmit={() => handleFetchGrid()}
          onClose={() => setUploadDividendModal(false)}
        />
      )}
    </React.Fragment>
  )
}

export default CustomCorporateActionPage
